import { SuiteProductAccess } from '../../components/suite/SuiteProductAccess';
import { cmsReturnPath } from '../../helpers/authNavigation';
import { centralEnabled, accountsPortal, refreshCmsSession } from '../../services/centralAuth';
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCircleCheck, faExclamationTriangle, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { appEnv } from "../../config/env";
import { SiteSwitcher } from "../../services/SiteSwitcher";
import { AvailableSite } from "../../models/AppModels";
import { TokenService } from "../../services/TokenService";

const SelectSite = (): JSX.Element => {
  const [sites, setSites] = useState<Array<AvailableSite>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const siteSwitcherRef = useRef<SiteSwitcher>(new SiteSwitcher());
  const tokenSvcRef = useRef<TokenService>(new TokenService());
  const previousSelectedSiteRef = useRef<string | null>(null);
  const nav = useNavigate();
  const location = useLocation();

  const getNavToUrl = (siteId: string = selectedSite): string => {
    if (centralEnabled) return cmsReturnPath(location.state?.returnTo);
    const storedurl = window.localStorage.getItem("LastUrl");
    const previousSelectedSiteId = previousSelectedSiteRef.current;

    if (!previousSelectedSiteId || !siteId || previousSelectedSiteId !== siteId) {
      window.localStorage.removeItem("LastUrl");
      return "/content";
    }

    if (storedurl) {
      try {
        const parsedUrl = new URL(storedurl, window.location.origin);
        const path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        return path.toLowerCase() === "/select-site" ? "/content" : path;
      } catch {
        return "/content";
      }
    }

    return "/content";
  };

  const getAvailableSites = async (): Promise<void> => {
    setLoading(true);
    setErrorMessage("");
    const res = await siteSwitcherRef.current.getAvailableSites();

    if (res.length === 0) {
      if (centralEnabled) {
        const session = await refreshCmsSession();
        if (session.context.workspaces.length > 0) throw new Error("Your site access exists, but CMS could not load your sites. Please retry.");
      }
      setSites([]);
      setErrorMessage("");
      setLoading(false);
      return;
    }

    const existingSelection = siteSwitcherRef.current.getSelectedSiteKey();
    previousSelectedSiteRef.current = existingSelection;
    const initialSelection = existingSelection && res.some(site => site.SiteId === existingSelection)
      ? existingSelection
      : res[0].SiteId;

    setSites(res);
    setSelectedSite(initialSelection);
    siteSwitcherRef.current.setSelectedSiteKey(initialSelection);
    setLoading(false);
  };

  const handleSiteSelect = (siteId: string): void => {
    const shouldAutoContinue = !!selectedSite && siteId !== selectedSite;

    setSelectedSite(siteId);
    siteSwitcherRef.current.setSelectedSiteKey(siteId);

    if (shouldAutoContinue) {
      nav(getNavToUrl(siteId), { replace: true });
    }
  };

  const handleContinue = (): void => {
    if (!selectedSite) {
      setErrorMessage("Choose the site you want to work with before continuing.");
      return;
    }

    nav(getNavToUrl(), { replace: true });
  };

  useEffect(() => {
    if (tokenSvcRef.current.hasTokenExpired()) {
      nav("/signin", { replace: true });
      return;
    }

    void getAvailableSites().catch(error => { setErrorMessage(error instanceof Error ? error.message : "Unable to retrieve sites. Please retry."); setLoading(false); });
  }, []);

  if (centralEnabled && !loading && !errorMessage && sites.length === 0) return <SuiteProductAccess product="cms" />;

  return (
    <div className="signin-shell">
      <Row className="justify-content-center">
        <Col xl={10} lg={11}>
          <div className="signin-card">
            <div className="signin-card__intro">
              <span className="signin-card__eyebrow">Site access</span>
              <h1 className="signin-card__title">Choose the site you want to manage first.</h1>
              <p className="signin-card__description">
                Your account has access to more than one site. Pick the one you want to open now and you can switch again later from the header.
              </p>

              <div className="info-panel mb-3">
                <div className="info-panel__content">
                  <div className="info-panel__title">Why this step appears</div>
                  <div className="info-panel__body">
                    We need an active site context before loading content, layouts, metadata and settings.
                  </div>
                </div>
              </div>

              <div className="info-panel info-panel--success mb-0">
                <div className="info-panel__content">
                  <div className="info-panel__title">You can switch later</div>
                  <div className="info-panel__body">
                    This only sets your initial workspace after sign-in. It does not limit access to your other sites.
                  </div>
                </div>
              </div>
            </div>

            <div className="signin-card__panel">
              <div className="signin-form__header">
                <span className="signin-form__badge">Site selection</span>
                <h2 className="signin-form__title">Select your workspace</h2>
                <p className="signin-form__text">Choose one site to continue into the admin area.</p>
              </div>

              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-secondary" role="status" aria-hidden="true"></div>
                  <div className="mt-3">Retrieving available sites...</div>
                </div>
              )}

              {!loading && errorMessage && sites.length === 0 && (
                <Alert variant="danger" className="signin-alert">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{errorMessage}</span>
                </Alert>
              )}

              {centralEnabled && !loading && <p><a href={`${accountsPortal}/cms`}>Your CMS access in Accounts</a> · <a href="/users/registernewsite">Create a site</a></p>}
              {!loading && sites.length > 0 && (
                <>
                  {errorMessage && (
                    <Alert variant="warning" className="signin-alert">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <span>{errorMessage}</span>
                    </Alert>
                  )}

                  <div className="business-select-list">
                    {sites.map(site => {
                      const isSelected = selectedSite === site.SiteId;

                      return (
                        <button
                          key={site.SiteId}
                          type="button"
                          className={`business-select-card${isSelected ? " business-select-card--active" : ""}`}
                          onClick={() => handleSiteSelect(site.SiteId)}
                        >
                          <div className="business-select-card__main">
                            <div className="business-select-card__icon">
                              <FontAwesomeIcon icon={faGlobe} />
                            </div>
                            <div className="business-select-card__content">
                              <div className="business-select-card__title-row">
                                <span className="business-select-card__title">{site.SiteName || "Unnamed site"}</span>
                                {isSelected && (
                                  <span className="business-select-card__status">
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                    <span>Selected</span>
                                  </span>
                                )}
                              </div>
                              <div className="business-select-card__meta">Site workspace</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="signin-form__actions signin-form__actions--split">
                    <div className="signin-form__text mb-0">
                      {selectedSite
                        ? `${sites.find(site => site.SiteId === selectedSite)?.SiteName ?? "Site"} will open first.`
                        : "Select a site to continue."}
                    </div>
                    <Button variant="primary" size="lg" onClick={handleContinue}>
                      <span>Continue&nbsp;</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SelectSite;
